import { useAdminAccess } from "../hooks/useAdminAccess";
import UserDiagnostics from "./UserDiagnostics";
import { adminRecords } from "./adminRecords";
import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import * as Sentry from "@sentry/react";
import firebase from "firebase/app";
import { useCallback, useEffect, useMemo, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import CopyToClipboard from "react-copy-to-clipboard";
import { JSONTree } from "react-json-tree";
import { NavLink, Route, Switch, useHistory, useLocation, useParams } from "react-router-dom";
import Select from "react-select";
import { DEFAULT_PRICING_VERSION, planToString } from "../../../src/shared/constants";
import Auth from "../Auth";
import CompletionsSearch from "../components/Admin/CompletionsSearch";
import CollapsableContainer from "../components/CollapsableContainer";
import StripeStatus from "../components/StripeStatus";
import { useToast } from "../components/ToastWrapper";
import { Dialog, DialogContent } from "../sudokit/components/Dialog";
import { SudoButton } from "../sudokit/components/SudoButton";
import { SudoTooltip } from "../sudokit/components/SudoTooltip";
import Utils, { limitDecimals } from "../Utils";
import CardSets from "./CardSets";
import Completions from "./Completions";
import Documents from "./Documents";
import Invocations from "./Invocations";
import Ledgers from "./Ledgers";
import { getCurrentAdminLedgerId } from "./ledgerUtils";
import { PartnerCodeDetails } from "./PartnerCodes/PartnerCodeDetails";
import { usePartnerCodeByCode } from "./PartnerCodes/PartnerCodesQueries";
import Projects from "./Projects";
import Series from "./Series";
import SharedDocuments from "./SharedDocuments";
import StripeEmailCollisionWarning from "./StripeEmailCollisionWarning";
import StripeLinkedAccounts from "./StripeLinkedAccounts";
import { ManagePlans } from "./User/ManagePlans";
import { CustomerIoActions } from "./User/CustomerIoActions";
import { TrialFreeChatTesting } from "./User/TrialFreeChatTesting";
import { AnnualCreditsRepair } from "./User/AnnualCreditsRepair";
import { TrialStatusToggle } from "./User/TrialStatusToggle";
import { ConferencePaywall } from "./User/ConferencePaywall";
import { getConferenceCampaign, requiresConferencePayment } from "../../../src/shared/conferenceCampaign";
import { TrialAIReview } from "./User/TrialAIReview";
import UserFieldGrid from "./UserFieldGrid";
import UserInsights from "./UserInsights";
import UserNotes from "./UserNotes";
import { AgenticChatList, AgenticChatDetailModal } from "./AgenticChats";

import { formatDateTimeWithTimezone } from "../utils/formatDateWithTimezone";
var dateFormat = require("dateformat");


const AUDIT_LEVELS = [
  { value: 0, label: "0 (sample first 100 requests)" },
  { value: 1, label: "1 (sample first 500 requests)" },
];

export default function User(props) {
  let { emailOrId } = useParams();
  const { can, role } = useAdminAccess();
  const adminTools = can("admin.tools");
  const canReadCardContent = can("cardsets.readContent");
  const canReadChats = can("chats.readContent");
  const confirmAction = useConfirmation(emailOrId);

  const [trialPeriodEnd, setTrialPeriodEnd] = useState(new Date());
  const [newTrialPeriodEnd, setNewTrialPeriodEnd] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [quota, setQuota] = useState(null);
  const [newQuota, setNewQuota] = useState(null);
  const [packQuota, setPackQuota] = useState(null);
  const [newPackQuota, setNewPackQuota] = useState(null);
  const [throttled, setThrottled] = useState(null);

  const [scimBlocked, setScimBlocked] = useState(null);
  const [scimThreshold, setScimThreshold] = useState(null);
  const [scimBypass, setScimBypass] = useState(null);
  const [newScimBlocked, setNewScimBlocked] = useState(null);
  const [newScimThreshold, setNewScimThreshold] = useState(null);
  const [newScimBypass, setNewScimBypass] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [loggingOutSessions, setLoggingOutSessions] = useState(false);

  const [chargeForPluginTesting, setChargeForPluginTesting] = useState(null);
  const [newChargeForPluginTesting, setNewChargeForPluginTesting] = useState(null);
  const [showEditChargeForPluginTesting, setShowEditChargeForPluginTesting] = useState(false);

  const [newDisplayName, setNewDisplayName] = useState("");
  const [showEditDisplayName, setShowEditDisplayName] = useState(false);
  const [displayNameMessage, setDisplayNameMessage] = useState("");

  const [newDiscordId, setNewDiscordId] = useState("");
  const [showEditDiscordId, setShowEditDiscordId] = useState(false);
  const [discordIdMessage, setDiscordIdMessage] = useState("");

  const [rollover, setRollover] = useState(null);
  const [newThrottled, setNewThrottled] = useState(null);
  const [newRollover, setNewRollover] = useState(null);
  const [totalCardsets, setTotalCardsets] = useState(null);
  const [subscriptionEvents, setSubscriptionEvents] = useState(null);

  const [showEdit, setShowEdit] = useState(false);
  const [showEditQuota, setShowEditQuota] = useState(false);
  const [showEditPackQuota, setShowEditPackQuota] = useState(false);
  const [showEditThrottled, setShowEditThrottled] = useState(false);
  const [showEditScim, setShowEditScim] = useState(false);
  const [showEditScimBypass, setShowEditScimBypass] = useState(false);
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [showRollover, setShowRollover] = useState(false);
  const [newEmailMessage, setNewEmailMessage] = useState(false);

  const [error, setError] = useState(false);

  const [auditLevel, setAuditLevel] = useState(null);
  const [showEditAudit, setShowEditAudit] = useState(false);
  const [user, setUser] = useState(null);
  const [trialDuration, setTrialDuration] = useState(null);
  const [daysTillEnd, setDaysTillEnd] = useState(null);

  const [toolsEnabled, setToolsEnabled] = useState(true);
  const [creatingDemoProject, setCreatingDemoProject] = useState(false);
  const [demoProject, setDemoProject] = useState(null);

  const [isMobileUser, setIsMobileUser] = useState(false);

  const [link, setLink] = useState(null);
  const [ledgerBalance, setLedgerBalance] = useState(null);
  const [ledgerBalanceLoading, setLedgerBalanceLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [viewingPartnerCode, setViewingPartnerCode] = useState(null);
  const [selectedSubscriptionEvent, setSelectedSubscriptionEvent] = useState(null);
  const [viewingChatId, setViewingChatId] = useState(null);

  const [activeCheckoutSessions, setActiveCheckoutSessions] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [syncingTaxInformation, setSyncingTaxInformation] = useState(false);

  const [facebookDeletionStatus, setFacebookDeletionStatus] = useState(null);
  const [facebookDeletionLoading, setFacebookDeletionLoading] = useState(false);
  const [facebookDeletionError, setFacebookDeletionError] = useState(null);
  const [facebookDeletionActionLoading, setFacebookDeletionActionLoading] = useState(false);

  const [restoringToTrial, setRestoringToTrial] = useState(false);
  const [clearingAnnualOnly, setClearingAnnualOnly] = useState(false);

  // My Voice onboarding tooltip toggle
  const [myVoiceOnboarding, setMyVoiceOnboarding] = useState(null);
  const [newMyVoiceOnboarding, setNewMyVoiceOnboarding] = useState(null);
  const [showEditMyVoiceOnboarding, setShowEditMyVoiceOnboarding] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const { setToast } = useToast();

  const formatDateTime = useCallback((value) => {
    if (!value) {
      return "Unknown";
    }
    const serializedSeconds = typeof value === "object" ? value.seconds ?? value._seconds : null;
    const normalizedValue = value?.toDate ? value.toDate() : serializedSeconds !== null && serializedSeconds !== undefined ? new Date(serializedSeconds * 1000) : value;
    const date = new Date(normalizedValue);
    if (Number.isNaN(date.getTime())) {
      return typeof value === "string" ? value : "Unknown";
    }
    return date.toLocaleString();
  }, []);

  const loadFacebookDeletionStatus = useCallback(async () => {
    if (!adminTools || !user?.uid) {
      setFacebookDeletionStatus(null);
      setFacebookDeletionError(null);
      return;
    }

    setFacebookDeletionLoading(true);
    setFacebookDeletionError(null);

    try {
      const response = await Auth.fetchWithIdToken("/api/admin/facebook-deletion/user-status", {
        uid: user.uid,
      });

      if (response.success) {
        setFacebookDeletionStatus(response.status || null);
      } else {
        setFacebookDeletionStatus(null);
        setFacebookDeletionError(response.message || "Failed to load Facebook deletion status");
      }
    } catch (err) {
      setFacebookDeletionStatus(null);
      setFacebookDeletionError(err?.message || "Failed to load Facebook deletion status");
    } finally {
      setFacebookDeletionLoading(false);
    }
  }, [user?.uid, adminTools]);

  useEffect(() => {
    loadFacebookDeletionStatus();
  }, [loadFacebookDeletionStatus]);

  // Open agentic chat detail when linked from an invocation (e.g. /admin/user/.../chats?openChat=<id>).
  useEffect(() => {
    if (!canReadChats || !user?.uid) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const openChat = params.get("openChat");
    if (!openChat) {
      return;
    }
    setViewingChatId(openChat);
    params.delete("openChat");
    const newSearch = params.toString();
    const pathWithQuery = `${location.pathname}${newSearch ? `?${newSearch}` : ""}${location.hash || ""}`;
    history.replace(pathWithQuery);
  }, [user?.uid, canReadChats, location.search, location.pathname, location.hash, history]);

  const handleCancelFacebookDeletion = useCallback(async () => {
    if (!user?.uid) {
      return;
    }
    await confirmAction({
      title: "Cancel scheduled deletion",
      message: "Cancel the scheduled deletion and resume this user's subscription?",
      actionLabel: "Resume subscription",
      destructive: false
    }, async () => {
      setFacebookDeletionActionLoading(true);
      try {
        const response = await Auth.fetchWithIdToken("/api/admin/facebook-deletion/user-cancel", {
          uid: user.uid
        });
        if (!response.success) {
          throw new Error(response.message || "Failed to cancel scheduled deletion");
        }
        setToast({
          type: "success",
          message: "Scheduled Facebook deletion cancelled"
        });
        await loadFacebookDeletionStatus();
      } catch (err) {
        setToast({
          type: "error",
          message: err?.message || "Failed to cancel scheduled deletion"
        });
      } finally {
        setFacebookDeletionActionLoading(false);
      }
    });
  }, [user?.uid, loadFacebookDeletionStatus, setToast, confirmAction]);

  let subscription, membership;

  const onToolButtonClick = useCallback((url, params) => {
    setToolsEnabled(false);
    Auth.fetchWithIdToken(url, params)
      .then((res) => {
        setToolsEnabled(true);
        window.location.reload();
      })
      .catch((e) => {
        setToolsEnabled(true);
        alert(e.message);
      });
  }, []);

  const fetchActiveCheckoutSessions = useCallback(async () => {
    if (!user || !user.subscription) return;

    setLoadingSessions(true);
    try {
      const response = await Auth.fetchWithIdToken(`/api/subscriptions/${user.uid}/listCheckoutSessions`, {});
      setActiveCheckoutSessions(response.sessions);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLoadingSessions(false);
    }
  }, [user]);

  const expireAllCheckoutSessions = useCallback(async () => {
    if (!user || !user.subscription) return;

    try {
      setToolsEnabled(false);
      await Auth.fetchWithIdToken(`/api/subscriptions/${user.uid}/expireCheckoutSessions`, {});
      await fetchActiveCheckoutSessions();
      setToolsEnabled(true);
    } catch (error) {
      Sentry.captureException(error);
      setToolsEnabled(true);
      alert(`Error expiring all checkout sessions: ${error.message}`);
    }
  }, [user, fetchActiveCheckoutSessions]);

  const expireSpecificSession = useCallback(
    async (sessionId) => {
      if (!user || !user.subscription) return;

      try {
        setToolsEnabled(false);
        await Auth.fetchWithIdToken(`/api/subscriptions/${user.uid}/expireSpecificSession`, {
          sessionId,
        });
        // Refresh the sessions list
        await fetchActiveCheckoutSessions();
        setToolsEnabled(true);
      } catch (error) {
        Sentry.captureException(error);
        setToolsEnabled(true);
        alert(`Error expiring session: ${error.message}`);
      }
    },
    [user, fetchActiveCheckoutSessions]
  );

  async function syncTaxInformation() {
    if (!user?.uid || !subscription) {
      return;
    }

    setSyncingTaxInformation(true);
    setToolsEnabled(false);

    try {
      const response = await Auth.fetchWithIdToken(`/api/admin/user/${user.uid}/sync-tax-information`, { method: "POST" });
      alert(response.message || "Tax information synced.");
      window.location.reload();
    } catch (err) {
      alert(err?.message || "Failed to sync tax information.");
      setToolsEnabled(true);
      setSyncingTaxInformation(false);
    }
  }

  function saveAuditLevel(level) {
    Auth.fetchWithIdToken(`/api/admin/user/${user.uid}/settings`, { update: { auditLevel: level } })
      .then(() => {
        setAuditLevel(level);
      });
  }

  function fetchIsMobile(uid) {
    adminRecords.collection("userData")
      .doc(uid)
      .get()
      .then((doc) => {
        if (doc.exists) {
          const data = doc.data();
          setIsMobileUser(data.hasUsedMobile);
          setMyVoiceOnboarding(!!data.showMyVoiceWriteOnboarding);
          setNewMyVoiceOnboarding(!!data.showMyVoiceWriteOnboarding);
        } else {
          setIsMobileUser(false);
          setMyVoiceOnboarding(false);
          setNewMyVoiceOnboarding(false);
        }
      });
  }

  const updateEmailInURL = (newEmail) => {
    const currentPath = history.location.pathname;
    const pathSegments = currentPath.split("/");

    pathSegments[pathSegments.length - 1] = newEmail;

    const newPath = pathSegments.join("/");

    history.push(newPath);
  };

  function updateEmailInFirebase() {
    setUpdatingEmail(true);
    Auth.fetchWithIdToken("/api/admin/updateUserEmail", {
      uid: user.uid,
      newEmail,
    })
      .then(() => {
        updateEmailInURL(newEmail);
        setShowEditEmail(false);
        setNewEmailMessage("");
        setToast("Email updated successfully");
        setUpdatingEmail(false);
        setTimeout(() => {
          window.location.reload();
        }, 400);
      })
      .catch((e) => {
        setUpdatingEmail(false);
        setNewEmailMessage("Problem updating - check that another user with this email doesn't already exist");
      });
  }

  function updateDisplayNameInFirebase() {
    Auth.fetchWithIdToken("/api/admin/updateUserDisplayName", {
      uid: user.uid,
      displayName: newDisplayName,
    })
      .then(() => {
        // Update the user object in the component state
        setUser({
          ...user,
          displayName: newDisplayName,
        });
        setDisplayNameMessage("Name updated!");
      })
      .catch((e) => {
        setDisplayNameMessage("Problem updating name");
      });
  }

  function updateDiscordIdInFirebase() {
    Auth.fetchWithIdToken("/api/admin/updateUserDiscordId", {
      uid: user.uid,
      discordId: newDiscordId,
    })
      .then(() => {
        setDiscordIdMessage("Discord ID updated!");
        setShowEditDiscordId(false);
        getUser();
      })
      .catch((e) => {
        setDiscordIdMessage("Problem updating Discord ID");
      });
  }

  async function getUser() {
    let theUser = null;
    const decodedEmailOrId = decodeURIComponent(emailOrId);
    const paramIsEmail = decodedEmailOrId.includes("@");

    if (paramIsEmail) {
      const email = decodedEmailOrId;
      let users = await props.userCache.fetchEmails([email]);

      if (!users) {
        return setNotFound(true);
      }
      theUser = users[0];
    } else {
      await props.userCache.fetchIds([emailOrId]);
      theUser = props.userCache.users[emailOrId];
    }

    if (!theUser) {
      return setNotFound(true);
    }

    setUser(theUser);
    fetchIsMobile(theUser.uid);

    if (theUser.subscription) {
      let end = theUser.subscription.data.trialPeriodEnd;
      setTrialPeriodEnd(end);
      setAuditLevel(theUser.subscription.auditLevel());

      setQuota(theUser.subscription.data.quota);
      setPackQuota(theUser.subscription.data.packQuota);
      setThrottled(!!theUser.subscription.data.throttled);
      setScimThreshold(theUser.subscription.data.scimThreshold);
      setScimBlocked(!!theUser.subscription.data.scimBlocked);
      setScimBypass(!!theUser.subscription.data.scimBypass);
      setRollover(!!theUser.subscription.data.rollover);
      setChargeForPluginTesting(!!theUser.subscription.data.chargeForPluginTesting);
      setNewChargeForPluginTesting(!!theUser.subscription.data.chargeForPluginTesting);

      const events = await adminRecords.collection("subscriptionEvents").where("userId", "==", theUser.uid).orderBy("createdAt", "desc").get();

      setSubscriptionEvents(events.docs);

      const results = await Auth.fetchWithIdToken(`/api/subscriptions/${theUser.uid}/stats`);

      if (results.data) {
        setStats(results.data);
      }
    }

    // Full card records contain writing. Support sees a bounded metadata list in
    // diagnostics; only content-authorized users load records for this total.
    if (canReadCardContent) adminRecords.collection("cards")
      .where("userId", "==", theUser.uid)
      .get()
      .then((snap) => {
        setTotalCardsets(snap.docs.length);
      });
  }

  async function saveSubscription(e) {
    e.preventDefault();

    const update = {};
    const backendUpdate = {};

    if (newTrialPeriodEnd && newTrialPeriodEnd !== trialPeriodEnd) {
      update.trialPeriodEnd = newTrialPeriodEnd;
    }
    if (newQuota && newQuota !== quota) {
      // avoid NaN -> 0 quotas
      if (!Utils.isValidNum(newQuota)) {
        setError(true);
        setNewQuota(quota);
        return;
      }

      // TODO: when we switch to all backend queries,
      // change to regular update object
      backendUpdate.quota = parseInt(newQuota);
    }

    if (newPackQuota && newPackQuota !== packQuota) {
      // avoid NaN -> 0 quotas
      if (!Utils.isValidNum(newPackQuota)) {
        setError(true);
        setNewQuota(quota);
        return;
      }

      backendUpdate.packQuota = parseInt(newPackQuota);
    }
    if (newThrottled !== null && newThrottled !== throttled) {
      update.throttled = !!parseInt(newThrottled);
    }
    if (newScimBlocked !== null && newScimBlocked !== scimBlocked) {
      update.scimBlocked = !!parseInt(newScimBlocked);
    }
    if (newScimThreshold !== null && newScimThreshold !== scimThreshold) {
      update.scimThreshold = parseFloat(newScimThreshold);
    }
    if (newScimBypass !== null && newScimBypass !== scimBypass) {
      update.scimBypass = !!parseInt(newScimBypass);
    }
    if (newRollover !== null && newRollover !== rollover) {
      update.rollover = !!parseInt(newRollover);
    }
    if (newChargeForPluginTesting !== null && newChargeForPluginTesting !== chargeForPluginTesting) {
      update.chargeForPluginTesting = newChargeForPluginTesting;
    }

    if (Object.keys(backendUpdate).length) {
      await Auth.fetchWithIdToken(`/api/admin/user/${user.uid}`, { method: "PUT", update: backendUpdate });
    }

    if (newMyVoiceOnboarding !== null && newMyVoiceOnboarding !== myVoiceOnboarding) {
      update.showMyVoiceWriteOnboarding = !!newMyVoiceOnboarding;
    }
    if (Object.keys(update).length) {
      await Auth.fetchWithIdToken(`/api/admin/user/${user.uid}/settings`, { update });
    }

    // get and display updated user data
    await getUser();

    // reset input values
    if (backendUpdate.quota !== undefined) {
      setNewQuota(null);
    }

    if (backendUpdate.packQuota !== undefined) {
      setNewPackQuota(null);
    }

    if (newTrialPeriodEnd) {
      setNewTrialPeriodEnd(null);
    }
    if (newThrottled !== null) {
      setNewThrottled(null);
    }
    if (newScimBlocked !== null) {
      setNewScimBlocked(null);
    }
    if (newScimThreshold !== null) {
      setNewScimThreshold(null);
    }
    if (newScimBypass !== null) {
      setNewScimBypass(null);
    }
    if (newRollover !== null) {
      setNewRollover(null);
    }
    if (newMyVoiceOnboarding !== null) {
      setNewMyVoiceOnboarding(null);
    }

    setShowEdit(false);
    setShowEditQuota(false);
    setShowEditPackQuota(false);
    setShowEditThrottled(false);
    setShowEditScim(false);
    setShowEditScimBypass(false);
    setShowRollover(false);
    setShowEditChargeForPluginTesting(false);
    setShowEditMyVoiceOnboarding(false);

    setError(false);
  }

  async function clearRequiresSubscriptionFirst() {
    if (!user?.uid) return;
    await Auth.fetchWithIdToken(`/api/admin/user/${user.uid}/settings`, { update: { "features.requiresSubscriptionFirst": false } });
    await getUser();
    setToast("Cleared requiresSubscriptionFirst");
  }

  async function clearRequiresAnnualOnly() {
    if (!user?.uid || clearingAnnualOnly) return;
    setClearingAnnualOnly(true);
    try {
      await Auth.fetchWithIdToken(`/api/admin/user/${user.uid}/settings`, { update: { "features.requiresAnnualOnly": false } });
      delete props.userCache.users[user.uid];
      await getUser();
      setToast("Monthly plans are now available for this user");
    } catch (error) {
      Sentry.captureException(error);
      setToast("Could not clear the annual-only requirement. Please try again.");
    } finally {
      setClearingAnnualOnly(false);
    }
  }

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    const loadCurrentLedgerBalance = async () => {
      if (!user?.subscription || !user.subscription.usesLedgers || !user?.uid) {
        setLedgerBalance(null);
        setLedgerBalanceLoading(false);
        return;
      }
      setLedgerBalanceLoading(true);
      try {
        const ledgerId = getCurrentAdminLedgerId(user.subscription);
        const summary = await Auth.fetchWithIdToken(`/api/admin/ledger/${user.uid}/${ledgerId}`, {
          summaryOnly: true,
        });
        setLedgerBalance(typeof summary.balance === "number" ? summary.balance : null);
      } catch (err) {
        setLedgerBalance(null);
      } finally {
        setLedgerBalanceLoading(false);
      }
    };

    loadCurrentLedgerBalance();
  }, [user]);

  // Fetch active sessions when user data is loaded
  useEffect(() => {
    if (user && user.subscription) {
      fetchActiveCheckoutSessions();
    }
  }, [user, fetchActiveCheckoutSessions]);

  // Update trial end data
  useEffect(() => {
    if (user) {
      let now = new Date();
      let signedUp = new Date(user.metadata.creationTime);
      setTrialDuration(parseInt((trialPeriodEnd - signedUp) / 1000.0 / (60 * 60 * 24)));
      setDaysTillEnd(parseInt((trialPeriodEnd - now) / 1000.0 / (60 * 60 * 24)));
    }
  }, [trialPeriodEnd]);

  if (user) {
    subscription = user.subscription;

    if (subscription) {
      if (subscription.activeSubscription()) {
        membership = planToString(subscription.currentPlan());
      } else {
        membership = "No Membership";
      }
    }
  }

  const hasTaxLocationIssue = Boolean(subscription?.data?.taxLocationIssue || subscription?.data?.taxLocationIssueDetectedAt || subscription?.data?.taxLocationLastNotifiedAt);
  const hasTaxLocationBanner = Boolean(subscription?.data?.taxLocationIssue && !subscription?.data?.paused);
  const rawTotalQuota = subscription ? parseInt(subscription.totalQuota, 10) : null;
  const normalizedTotalQuota = rawTotalQuota !== null && !Number.isNaN(rawTotalQuota) ? rawTotalQuota : null;
  const ledgerBalanceForSync = typeof ledgerBalance === "number" ? ledgerBalance : null;
  const ledgerInSync = subscription?.usesLedgers ? (ledgerBalanceForSync !== null && normalizedTotalQuota !== null ? ledgerBalanceForSync === normalizedTotalQuota : false) : false;
  const ledgerSyncButtonDisabled = !toolsEnabled || ledgerBalanceLoading || !subscription?.usesLedgers || (ledgerBalanceForSync !== null && ledgerInSync);

  const openStripeCustomerLink = useCallback(() => {
    if (subscription) {
      window.open(`https://dashboard.stripe.com/customers/${subscription.data.stripeCustomerId}`);
    }
  }, [subscription]);

  const handleCreateDemoProject = async () => {
    if (!user) return;

    setCreatingDemoProject(true);
    try {
      const res = await Auth.fetchWithIdToken(`/api/admin/user/${user.uid}/createDemoProject`, {
        method: "POST",
      });

      const host = window.location.host;
      const projectUrl = `${window.location.protocol}//${host}/app/project/${res.projectId}`;
      setDemoProject({
        title: res.title,
        id: res.projectId,
        url: projectUrl,
      });
    } catch (error) {
      alert("Failed to create demo project: " + error.message);
    } finally {
      setCreatingDemoProject(false);
    }
  };

  // Build field configuration for UserFieldGrid
  const userFields = useMemo(() => {
    if (!user) return [];

    const fields = [
      // Account Info Category
      {
        key: "id",
        label: "ID",
        value: user.uid,
        category: "Account Info",
        copyable: true,
        showCopyIcon: false,
        alwaysShow: true,
        valueClassName: "font-mono break-all flex gap-1 items-start flex-col text-left [&_*]:text-left -ml-1",
      },
      {
        key: "email",
        label: "Email",
        value: user?.email,
        category: "Account Info",
        copyable: true,
        showCopyIcon: false,
        editable: true,
        onEdit: () => {
          setShowEditEmail(!showEditEmail);
          setNewEmailMessage("");
          setNewEmail("");
        },
        alwaysShow: true,
        className: "text-mono text-left [&_*]:text-left -ml-1",
        additionalContent: showEditEmail && (
          <div className="p-3 bg-white rounded border flex flex-col gap-2">
            <h4 className="text-sm font-medium mb-2">Set new email</h4>
            <input
              type="text"
              className="block px-3 py-2 border border-gray-300 rounded-md text-sm"
              onChange={(e) => {
                setNewEmail(e.target.value.trim());
              }}
              value={newEmail}
            />
            <SudoButton
              onClick={updateEmailInFirebase}
              variant="primary"
              size="small"
              state={updatingEmail ? "loading" : "default"}
              disabled={updatingEmail || !newEmail}
              aria-label="Save new email address"
            >
              {updatingEmail ? "Saving..." : "Save"}
            </SudoButton>
            {newEmailMessage && <div className="mt-2 text-xs text-green-600">{newEmailMessage}</div>}
          </div>
        ),
      },
      {
        key: "discordId",
        label: "Discord UID",
        value: subscription?.discordId,
        defaultValue: "Not set",
        category: "Account Info",
        type: "link",
        linkUrl: subscription?.discordId ? `https://discord.com/users/${subscription.discordId}` : undefined,
        linkNewTab: true,
        copyable: true,
        editable: true,
        onEdit: () => {
          setShowEditDiscordId(!showEditDiscordId);
          setDiscordIdMessage("");
          setNewDiscordId(subscription?.discordId || "");
        },
        alwaysShow: true,
        additionalContent: showEditDiscordId && (
          <div className="p-3 flex flex-col gap-2 bg-white rounded border">
            <h4 className="text-sm font-medium mb-2">Set Discord ID</h4>
            <input
              type="text"
              placeholder="Enter Discord user ID (leave empty to remove)"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm block"
              onChange={(e) => {
                setNewDiscordId(e.target.value.trim());
              }}
              value={newDiscordId}
            />
            <SudoButton
              onClick={updateDiscordIdInFirebase}
              variant="primary"
              size="small"
            >
              Save
            </SudoButton>
            {discordIdMessage && <div className="mt-2 text-xs text-green-600">{discordIdMessage}</div>}
          </div>
        ),
      },
      {
        key: "displayName",
        label: "Name",
        value: user.displayName,
        defaultValue: "Not set",
        category: "Account Info",
        editable: true,
        onEdit: () => {
          setShowEditDisplayName(!showEditDisplayName);
          setDisplayNameMessage("");
          setNewDisplayName(user.displayName || "");
        },
        alwaysShow: true,
        additionalContent: showEditDisplayName && (
          <div className="p-3 flex flex-col gap-2 bg-white rounded border">
            <h4 className="text-sm font-medium mb-2">Set new name</h4>
            <input
              type="text"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm block"
              onChange={(e) => {
                setNewDisplayName(e.target.value);
              }}
              value={newDisplayName}
            />
            <SudoButton
              onClick={updateDisplayNameInFirebase}
              variant="primary"
              size="small"
            >
              Save
            </SudoButton>
            {displayNameMessage && <div className="mt-2 text-xs text-green-600">{displayNameMessage}</div>}
          </div>
        ),
      },
    ];

    // Subscription Info Category - Always push all fields
    fields.push(
      {
        key: "signUpDate",
        label: "Sign Up Date",
        value: subscription ? dateFormat(subscription.data.signedUpAt, "mm/dd/yy") : null,
        category: "Subscription Info",
        alwaysShow: true,
      },
      {
        key: "subscriptionSource",
        label: "Last Subscription Source",
        value: subscription?.data?.subscriptionSource === "app_store" ? "App Store" : subscription?.data?.subscriptionSource === "play_store" ? "Play Store" : subscription ? "Web" : null,
        category: "Subscription Info",
        alwaysShow: true,
      },
      {
        key: "managedThrough",
        label: "Subscription Managed Through",
        value:
          subscription?.data?.subscriptionSource === "app_store"
            ? "iOS App Store"
            : subscription?.data?.subscriptionSource === "play_store"
            ? "Google Play Store"
            : subscription?.data?.subscriptionSource === "web"
            ? "Web (Stripe)"
            : subscription
            ? "Unknown"
            : null,
        category: "Subscription Info",
        alwaysShow: true,
      },
      {
        key: "sourcePlatform",
        label: "Checkout Origin Platform",
        value:
          subscription?.data?.source_platform === "mobile_ios"
            ? "iOS"
            : subscription?.data?.source_platform === "mobile_android"
            ? "Android"
            : subscription?.data?.source_platform === "web"
            ? "Web"
            : subscription
            ? "Unknown"
            : null,
        category: "Subscription Info",
        alwaysShow: true,
      },
      {
        key: "revenuecatUserId",
        label: "Revenuecat Customer",
        value: subscription?.data.revenuecatUserId || null,
        defaultValue: "No linked account",
        category: "Subscription Info",
        type: "link",
        linkUrl: subscription?.data.revenuecatUserId ? `https://app.revenuecat.com/customers/cbbca69a/${subscription.data.revenuecatUserId}` : undefined,
        linkNewTab: true,
        shortcut: subscription?.data.revenuecatUserId
          ? {
              key: "r",
              label: "Open",
              onTrigger: () => {
                window.open(`https://app.revenuecat.com/customers/cbbca69a/${subscription.data.revenuecatUserId}`);
              },
            }
          : undefined,
        alwaysShow: true,
      },
      {
        key: "stripeCustomerId",
        label: "Stripe Customer",
        value: subscription?.data.stripeCustomerId || null,
        category: "Subscription Info",
        type: "link",
        copyable: Boolean(subscription?.data.stripeCustomerId),
        linkUrl: subscription?.data.stripeCustomerId ? `https://dashboard.stripe.com/customers/${subscription.data.stripeCustomerId}` : undefined,
        linkNewTab: true,
        shortcut: subscription?.data.stripeCustomerId
          ? {
              key: "s",
              label: "Open",
              onTrigger: openStripeCustomerLink,
            }
          : undefined,
        alwaysShow: true,
      },
      {
        key: "langfuseTraces",
        label: "Langfuse Traces",
        value: subscription?.id ? "Go to Langfuse ↗" : null,
        category: "Subscription Info",
        type: "link",
        linkUrl: subscription?.id ? `https://cloud.langfuse.com/project/clxf4brjw0003y8x6yd7f7apr/users/${subscription.id}?tab=Traces` : undefined,
        linkNewTab: true,
        alwaysShow: subscription?.id ? true : false,
      },
      {
        key: "membership",
        label: "Membership",
        value: membership || null,
        category: "Subscription Info",
        alwaysShow: true,
        valueClassName: "font-medium",
      },
      {
        key: "adminRole",
        label: "Admin role",
        value: user?.adminRole || "None",
        category: "Subscription Info",
        alwaysShow: subscription ? true : false,
      }
    );

    // Active Subscription Fields
    fields.push(
      {
        key: "stripeStatus",
        label: "Stripe Status",
        value: subscription?.activeSubscription() ? subscription.data.stripeStatus : null,
        category: "Subscription Info",
        customRender: subscription?.activeSubscription() ? () => <StripeStatus status={subscription.data.stripeStatus} /> : undefined,
        alwaysShow: subscription?.activeSubscription() ? true : false,
      },
      {
        key: "currentPeriodEnd",
        label: "Current Period End",
        value: subscription?.activeSubscription() && subscription.data.currentPeriodEnd ? dateFormat(subscription.data.currentPeriodEnd, "mm/dd/yy") : null,
        category: "Subscription Info",
        alwaysShow: subscription?.activeSubscription() ? true : false,
      },
      {
        key: "cancelAt",
        label: "Cancel At",
        value: subscription?.activeSubscription() ? (subscription.data.cancelAt ? dateFormat(subscription.data.cancelAt, "mm/dd/yy") : "null") : null,
        category: "Subscription Info",
        alwaysShow: subscription?.activeSubscription() ? true : false,
      },
      {
        key: "canceledAt",
        label: "Canceled At",
        value: subscription?.activeSubscription() ? (subscription.data.canceledAt ? dateFormat(subscription.data.canceledAt, "mm/dd/yy") : "null") : null,
        category: "Subscription Info",
        alwaysShow: subscription?.activeSubscription() ? true : false,
      },
      {
        key: "paused",
        label: "Paused?",
        value: subscription?.activeSubscription()
          ? subscription.paused
            ? subscription.data.resumesAt
              ? `Yes (Resumes at ${dateFormat(subscription.data.resumesAt, "mm/dd/yy")})`
              : "Yes"
            : "No"
          : null,
        category: "Subscription Info",
        alwaysShow: subscription?.activeSubscription() ? true : false,
      },
      {
        key: "taxLocationIssue",
        label: "Tax Location Issue",
        value: hasTaxLocationBanner ? "Banner active" : hasTaxLocationIssue ? "Stale fields" : "Clear",
        category: "Subscription Info",
        alwaysShow: subscription ? true : false,
        customRender: subscription
          ? () => (
              <div className="flex flex-col gap-2">
                {hasTaxLocationBanner ? (
                  <span className="my-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 w-fit">Banner active</span>
                ) : hasTaxLocationIssue ? (
                  <span className="my-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 w-fit">Stale fields only</span>
                ) : (
                  <span className="my-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 w-fit">Clear</span>
                )}
                <span className="text-xs text-gray-700">Detected: {formatDateTime(subscription?.data?.taxLocationIssueDetectedAt)}</span>
                <span className="text-xs text-gray-700">Last notified: {formatDateTime(subscription?.data?.taxLocationLastNotifiedAt)}</span>
              </div>
            )
          : undefined,
      },
      {
        key: "referral",
        label: "Referral",
        value: subscription?.data.rewardfulId || "No",
        category: "Subscription Info",
        alwaysShow: subscription ? true : false,
      },
      {
        key: "pricingVersion",
        label: "Pricing Version",
        value: subscription?.data.version || null,
        category: "Subscription Info",
        alwaysShow: subscription ? true : false,
      },
      {
        key: "totalQuota",
        label: "Total Quota",
        value: subscription && normalizedTotalQuota !== null ? Utils.numberWithCommas(normalizedTotalQuota) : null,
        category: "Quota & Usage",
        alwaysShow: subscription ? true : false,
        valueClassName: subscription ? (normalizedTotalQuota < 0 ? "font-medium text-red-600 bg-red-50 rounded-md" : "font-medium") : "font-medium",
      },
      {
        key: "regularQuota",
        label: "Regular Quota",
        value: subscription && !subscription.usesLedgers ? Utils.numberWithCommas(quota) : null,
        category: "Quota & Usage",
        editable: subscription && !subscription.usesLedgers ? true : false,
        onEdit:
          subscription && !subscription.usesLedgers
            ? () => {
                setShowEditQuota(!showEditQuota);
                setNewQuota(showEditQuota ? quota : null);
              }
            : undefined,
        alwaysShow: subscription && !subscription.usesLedgers ? true : false,
        additionalContent:
          showEditQuota && subscription && !subscription.usesLedgers ? (
            <div className="p-3 bg-white rounded border">
              <h4 className="text-sm font-medium mb-2">Set a new quota (this amount resets and replaces quota)</h4>
              {error && <p className="text-red-600 text-xs mb-2">Invalid input, please try again</p>}
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                onChange={(e) => {
                  setNewQuota(e.target.value);
                }}
                value={newQuota}
              />
              <SudoButton
                onClick={saveSubscription}
                variant="primary"
                size="small"
              >
                Save
              </SudoButton>
            </div>
          ) : undefined,
      },
      {
        key: "packQuota",
        label: "Pack Quota",
        value: subscription && !subscription.usesLedgers ? packQuota : null,
        category: "Quota & Usage",
        editable: subscription && !subscription.usesLedgers ? true : false,
        onEdit:
          subscription && !subscription.usesLedgers
            ? () => {
                setShowEditPackQuota(!showEditPackQuota);
                setNewPackQuota(showEditPackQuota ? packQuota : null);
              }
            : undefined,
        alwaysShow: subscription && !subscription.usesLedgers ? true : false,
        additionalContent:
          showEditPackQuota && subscription && !subscription.usesLedgers ? (
            <div className="p-3 bg-white rounded border">
              <h4 className="text-sm font-medium mb-2">Adjust pack quota (this amount is added to the existing pack quota)</h4>
              {error && <p className="text-red-600 text-xs mb-2">Invalid input, please try again</p>}
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                onChange={(e) => {
                  setNewPackQuota(e.target.value);
                }}
                value={newPackQuota}
              />
              <SudoButton
                onClick={saveSubscription}
                variant="primary"
                size="small"
              >
                Save
              </SudoButton>
            </div>
          ) : undefined,
      },
      {
        key: "overageAllowed",
        label: "Overage Allowed?",
        value: subscription?.usesQuota ? (subscription.allowOverage ? "True" : "False") : null,
        category: "Quota & Usage",
        alwaysShow: subscription?.usesQuota ? true : false,
      },
      {
        key: "overage",
        label: "Overage",
        value: subscription?.usesQuota ? subscription.overage().words : null,
        category: "Quota & Usage",
        alwaysShow: subscription?.usesQuota ? true : false,
      },
      {
        key: "overageCost",
        label: "Overage Cost",
        value: subscription?.usesQuota ? `$${subscription.overage().cost / 100}` : null,
        category: "Quota & Usage",
        alwaysShow: subscription?.usesQuota ? true : false,
        valueClassName: "font-medium",
      }
    );

    fields.push(
      {
        key: "chargeForPluginTesting",
        label: "Charge For Plugin Testing",
        value: subscription?.data.chargeForPluginTesting ? "True" : "False",
        category: "Settings & Flags",
        editable: subscription ? true : false,
        onEdit: subscription
          ? () => {
              if (!showEditChargeForPluginTesting) {
                setNewChargeForPluginTesting(!!subscription.data.chargeForPluginTesting);
              }
              setShowEditChargeForPluginTesting(!showEditChargeForPluginTesting);
            }
          : undefined,
        alwaysShow: subscription ? true : false,
        additionalContent:
          showEditChargeForPluginTesting && subscription ? (
            <div className="p-3 flex flex-col gap-2 bg-white rounded border">
              <h4 className="text-sm font-medium mb-2">Set Charge For Plugin Testing</h4>
              <select
                value={newChargeForPluginTesting}
                onChange={(e) => {
                  setNewChargeForPluginTesting(e.target.value === "true");
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-2"
              >
                <option value="true">True</option>
                <option value="false">False</option>
              </select>
              <SudoButton
                onClick={saveSubscription}
                variant="primary"
                size="small"
              >
                Save
              </SudoButton>
            </div>
          ) : undefined,
      },
      {
        key: "myVoiceOnboarding",
        label: "My Voice Onboarding Tooltip",
        value: myVoiceOnboarding ? "True" : "False",
        category: "Settings & Flags",
        editable: true,
        bigField: true,
        onEdit: () => {
          if (!showEditMyVoiceOnboarding) {
            setNewMyVoiceOnboarding(!!myVoiceOnboarding);
          }
          setShowEditMyVoiceOnboarding(!showEditMyVoiceOnboarding);
        },
        alwaysShow: true,
        additionalContent: showEditMyVoiceOnboarding ? (
          <div className="p-3 flex flex-col gap-2 bg-white rounded border">
            <h4 className="text-sm font-medium mb-2">Show My Voice onboarding tooltip</h4>
            <select
              value={String(newMyVoiceOnboarding)}
              onChange={(e) => {
                setNewMyVoiceOnboarding(e.target.value === "true");
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-2"
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </select>
            <SudoButton
              onClick={saveSubscription}
              variant="primary"
              size="small"
            >
              Save
            </SudoButton>
          </div>
        ) : undefined,
      },
      {
        key: "throttled",
        label: "Throttled",
        value: throttled ? "True" : "False",
        category: "Settings & Flags",
        editable: subscription ? true : false,
        onEdit: subscription
          ? () => {
              if (!showEditThrottled) {
                setNewThrottled(!!subscription.data.throttled);
              }
              setShowEditThrottled(!showEditThrottled);
            }
          : undefined,
        alwaysShow: subscription ? true : false,
        additionalContent:
          showEditThrottled && subscription ? (
            <div className="p-3 flex flex-col gap-2 bg-white rounded border">
              <h4 className="text-sm font-medium mb-2">Set Throttled (0 = false, 1 = true)</h4>
              <input
                type="text"
                className="block px-3 py-2 border border-gray-300 rounded-md text-sm"
                onChange={(e) => {
                  setNewThrottled(e.target.value);
                }}
                value={newThrottled}
              />
              <SudoButton
                onClick={saveSubscription}
                variant="primary"
                size="small"
              >
                Save
              </SudoButton>
            </div>
          ) : undefined,
      },
      {
        key: "scimBlocked",
        label: "SCIM Blocked (applied SCIM check on all invocations)",
        value: scimBlocked ? `True (Threshold: ${scimThreshold || 0.5})` : `False (Threshold: ${scimThreshold || 0.5})`,
        category: "Settings & Flags",
        editable: subscription ? true : false,
        bigField: true,
        onEdit: subscription
          ? () => {
              if (!showEditScim) {
                setNewScimBlocked(!!subscription.data.scimBlocked);
              }
              setShowEditScim(!showEditScim);
            }
          : undefined,
        alwaysShow: subscription ? true : false,
        additionalContent:
          showEditScim && subscription ? (
            <div className="p-3 flex flex-col gap-2 bg-white rounded border">
              <h4 className="text-sm font-medium mb-2">Set Scim Blocked (0 = false, 1 = true)</h4>
              <input
                type="text"
                className="block px-3 py-2 border border-gray-300 rounded-md text-sm mb-3"
                onChange={(e) => {
                  setNewScimBlocked(e.target.value);
                }}
                value={newScimBlocked}
              />
              <h4 className="text-sm font-medium mb-2">Set Scim Threshold (blank = 0.5)</h4>
              <input
                type="text"
                className="block px-3 py-2 border border-gray-300 rounded-md text-sm"
                onChange={(e) => {
                  setNewScimThreshold(e.target.value);
                }}
                value={newScimThreshold}
              />
              <SudoButton
                onClick={saveSubscription}
                variant="primary"
                size="small"
              >
                Save
              </SudoButton>
            </div>
          ) : undefined,
      },
      {
        key: "scimBypass",
        label: "SCIM Bypass (skips SCIM check. this overrides SCIM blocked.)",
        value: scimBypass ? "True" : "False",
        category: "Settings & Flags",
        editable: subscription ? true : false,
        bigField: true,
        onEdit: subscription
          ? () => {
              if (!showEditScimBypass) {
                setNewScimBypass(!!subscription.data.scimBypass);
              }
              setShowEditScimBypass(!showEditScimBypass);
            }
          : undefined,
        alwaysShow: subscription ? true : false,
        additionalContent:
          showEditScimBypass && subscription ? (
            <div className="p-3 flex flex-col gap-2 bg-white rounded border">
              <h4 className="text-sm font-medium mb-2">Set Scim Bypass (0 = false, 1 = true)</h4>
              <input
                type="text"
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                onChange={(e) => {
                  setNewScimBypass(e.target.value);
                }}
                value={newScimBypass}
              />
              <SudoButton
                onClick={saveSubscription}
                variant="primary"
                size="small"
              >
                Save
              </SudoButton>
            </div>
          ) : undefined,
      },
      {
        key: "rollover",
        label: "Rollover (whether quota will rollover on next cycle)",
        value: rollover ? "True" : "False",
        category: "Settings & Flags",
        editable: subscription ? true : false,
        bigField: true,
        onEdit: subscription
          ? () => {
              if (!showRollover) {
                setNewRollover(!!subscription.data.rollover);
              }
              setShowRollover(!showRollover);
            }
          : undefined,
        alwaysShow: subscription ? true : false,
        additionalContent:
          showRollover && subscription ? (
            <div className="p-3 flex flex-col gap-2 bg-white rounded border">
              <h4 className="text-sm font-medium mb-2">Set Rollover (0 = false, 1 = true)</h4>
              <input
                type="text"
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                onChange={(e) => {
                  setNewRollover(e.target.value);
                }}
                value={newRollover}
              />
              <SudoButton
                onClick={saveSubscription}
                variant="primary"
                size="small"
              >
                Save
              </SudoButton>
            </div>
          ) : undefined,
      }
    );

    // This total comes from the content query above. Hide it when that query is
    // forbidden; diagnostics reports its own bounded metadata count for Support.
    if (canReadCardContent) {
      fields.push({
        key: "totalCardsets",
        label: "Total Cardsets Requested",
        value: totalCardsets !== null ? totalCardsets : "Loading...",
        category: "Stats & Metrics",
        alwaysShow: true,
      });
    }

    fields.push(
      {
        key: "totalInferenceCost",
        label: "Total Inference Cost (since Sep 2021)",
        value: user.cost ? `$${limitDecimals(user.cost || 0, 2)}` : null,
        category: "Stats & Metrics",
        valueClassName: "font-medium",
        alwaysShow: user.cost ? true : false,
      },
      {
        key: "totalPaidInvoices",
        label: "Total Paid Invoices",
        value: `$${user.paid || 0}`,
        category: "Stats & Metrics",
        alwaysShow: true,
        valueClassName: "font-medium",
      },
      {
        key: "totalRefunds",
        label: "Total Refunds",
        value: stats ? `$${stats.refunds}` : null,
        category: "Stats & Metrics",
        alwaysShow: stats ? true : false,
        valueClassName: "font-medium",
      }
    );

    fields.push(
      {
        key: "trialPeriodEnd",
        label: "Trial Period End",
        value: subscription && !subscription.usesQuota ? `${dateFormat(trialPeriodEnd, "mm/dd/yy")} (${daysTillEnd < 0 ? "Ended" : `${daysTillEnd} days left`})` : null,
        category: "Trial & Audit",
        editable: subscription && !subscription.usesQuota ? true : false,
        onEdit:
          subscription && !subscription.usesQuota
            ? () => {
                if (!showEdit) {
                  setNewTrialPeriodEnd(trialPeriodEnd);
                }
                setShowEdit(!showEdit);
              }
            : undefined,
        alwaysShow: subscription && !subscription.usesQuota ? true : false,
        additionalContent:
          showEdit && subscription && !subscription.usesQuota ? (
            <div className="p-3 bg-white rounded border">
              <h4 className="text-sm font-medium mb-2">Set a new trial end date</h4>
              <p className="text-xs text-gray-600 mb-3">Note: This has no effect if they have an active membership. Changes take place immediately.</p>
              <Calendar
                onChange={setNewTrialPeriodEnd}
                value={newTrialPeriodEnd}
              />
              <SudoButton
                onClick={saveSubscription}
                variant="primary"
                size="small"
              >
                Save
              </SudoButton>
            </div>
          ) : undefined,
      },
      {
        key: "trialDuration",
        label: "Trial Duration",
        value: subscription && !subscription.usesQuota ? `${trialDuration} days` : null,
        category: "Trial & Audit",
        alwaysShow: subscription && !subscription.usesQuota ? true : false,
      },
      {
        key: "onTrial",
        label: "On Trial?",
        value: subscription?.usesQuota ? (subscription.activeTrial() ? "Yes" : "No") : null,
        category: "Trial & Audit",
        alwaysShow: subscription?.usesQuota ? true : false,
      },
      {
        key: "auditLevel",
        label: "Audit Level",
        value: subscription ? auditLevel : null,
        category: "Trial & Audit",
        editable: subscription ? true : false,
        onEdit: subscription
          ? () => {
              setShowEditAudit(!showEditAudit);
            }
          : undefined,
        alwaysShow: subscription ? true : false,
        additionalContent:
          showEditAudit && subscription ? (
            <div className="p-3 bg-white rounded border h-32">
              <Select
                value={auditLevel}
                onChange={(selectOption) => {
                  saveAuditLevel(selectOption.value);
                }}
                options={AUDIT_LEVELS}
              />
            </div>
          ) : undefined,
      },
      {
        key: "hasUsedMobile",
        label: "Has used Mobile?",
        value: isMobileUser ? "Yes" : "No",
        category: "Trial & Audit",
        alwaysShow: true,
      },
      {
        key: "featureGates",
        label: "⚠️ Active Feature Gates",
        value: subscription?.features && Object.keys(subscription.features).length > 0 ? "See badges" : subscription?.features ? "No gates found" : null,
        category: "Trial & Audit",
        alwaysShow: subscription?.features ? true : false,
        customRender: subscription?.features
          ? () => (
              <>
                {Object.keys(subscription.features).length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(subscription.features).map(([gateName, isActive]) => (
                      <span
                        key={gateName}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
                      >
                        {gateName}
                        {isActive && <span className="ml-1 text-green-600">●</span>}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-600">No gates found</span>
                )}
              </>
            )
          : undefined,
      },
      {
        key: "requiresSubscriptionFirst",
        label: "Requires Subscription First (Ad Paywall)",
        value: subscription?.features?.requiresSubscriptionFirst ? "Active" : "Not active",
        category: "Trial & Audit",
        alwaysShow: subscription?.features?.requiresSubscriptionFirst ? true : false,
        customRender: subscription?.features?.requiresSubscriptionFirst
          ? () => (
              <div className="flex flex-col gap-2">
                <span className="my-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 w-fit">
                  Active {!subscription?.hasPaid && " — User has not paid"}
                </span>
                {!subscription?.hasPaid && (
                  <SudoButton
                    onClick={clearRequiresSubscriptionFirst}
                    variant="secondary"
                    size="small"
                  >
                    Clear Paywall Requirement
                  </SudoButton>
                )}
              </div>
            )
          : undefined,
      },
      {
        key: "conferencePaywall",
        label: "Author Nation Paywall",
        value: getConferenceCampaign(subscription?.data?.conferenceCampaign?.id)
          ? (requiresConferencePayment(subscription.data) ? "Active" : "Not active") : null,
        category: "Trial & Audit",
        alwaysShow: !!getConferenceCampaign(subscription?.data?.conferenceCampaign?.id),
        customRender: () => <ConferencePaywall
          userId={user.uid}
          subscription={subscription}
          setToast={setToast}
          canAllowTrial={can("users.updateBilling")}
          onUpdated={async () => {
            delete props.userCache.users[user.uid];
            await getUser();
          }}
        />,
      },
      {
        key: "requiresAnnualOnly",
        label: "Requires Annual Only",
        value: subscription?.features?.requiresAnnualOnly ? "Active" : "Not active",
        category: "Trial & Audit",
        alwaysShow: !!subscription?.features?.requiresAnnualOnly,
        customRender: subscription?.features?.requiresAnnualOnly
          ? () => (
              <div className="flex flex-col gap-2">
                <span className="my-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 w-fit">
                  Active {!subscription?.hasPaid && " — User has not paid"}
                </span>
                {!subscription?.hasPaid && (
                  <SudoButton
                    onClick={clearRequiresAnnualOnly}
                    variant="secondary"
                    size="small"
                    disabled={clearingAnnualOnly}
                    state={clearingAnnualOnly ? "loading" : "default"}
                  >
                    Allow Monthly Plans
                  </SudoButton>
                )}
              </div>
            )
          : undefined,
      }
    );

    fields.push(
      {
        key: "partnerCode",
        label: "Partner Code",
        value: subscription?.data.partnerCode || null,
        defaultValue: "No partner code",
        category: "Partner Program",
        onClick: subscription?.data.partnerCode
          ? () => {
              setViewingPartnerCode(subscription.data.partnerCode);
            }
          : undefined,
        alwaysShow: subscription ? true : false,
        className: "font-mono",
      },
      {
        key: "partnerCreditAmount",
        label: "Partner Code Amount",
        value: subscription?.data.partnerCreditAmount ? `${Utils.numberWithCommas(subscription.data.partnerCreditAmount)} credits` : null,
        defaultValue: "N/A",
        category: "Partner Program",
        alwaysShow: subscription ? true : false,
        valueClassName: "font-medium",
      },
      {
        key: "partnerCreditStatus",
        label: "Partner Code Status",
        value: subscription?.data?.partnerCreditStatus ? subscription.data?.partnerCreditStatus?.charAt(0).toUpperCase() + subscription.data?.partnerCreditStatus?.slice(1) : null,
        defaultValue: "N/A",
        category: "Partner Program",
        alwaysShow: subscription ? true : false,
        valueClassName: "font-medium",
      }
    );

    // Read-only fields remain useful to Support. Restrict configuration and
    // identity mutations before rendering their edit controls or shortcuts.
    const adminOnly = new Set(["langfuseTraces"]);
    const accountFields = new Set(["email", "displayName", "discordId"]);
    return fields.filter(field => adminTools || !adminOnly.has(field.key)).map(field => {
      const targetIsAdmin = !!user?.adminRole;
      const identityBlocked = accountFields.has(field.key) && (!can("users.updateAccount") || (role === "support" && targetIsAdmin));
      if (identityBlocked) return { ...field, editable: false, onEdit: undefined, additionalContent: undefined };
      if (!adminTools && field.key === "partnerCode") return { ...field, onClick: undefined };
      return field;
    });
  }, [
    role, adminTools, can,
    user,
    subscription,
    membership,
    hasTaxLocationIssue,
    hasTaxLocationBanner,
    formatDateTime,
    showEditEmail,
    newEmail,
    newEmailMessage,
    updatingEmail,
    clearingAnnualOnly,
    showEditDiscordId,
    newDiscordId,
    discordIdMessage,
    showEditDisplayName,
    newDisplayName,
    displayNameMessage,
    openStripeCustomerLink,
    quota,
    showEditQuota,
    newQuota,
    error,
    packQuota,
    showEditPackQuota,
    newPackQuota,
    chargeForPluginTesting,
    showEditChargeForPluginTesting,
    newChargeForPluginTesting,
    myVoiceOnboarding,
    showEditMyVoiceOnboarding,
    newMyVoiceOnboarding,
    throttled,
    showEditThrottled,
    newThrottled,
    scimBlocked,
    scimThreshold,
    showEditScim,
    newScimBlocked,
    newScimThreshold,
    scimBypass,
    showEditScimBypass,
    newScimBypass,
    rollover,
    showRollover,
    newRollover,
    totalCardsets,
    stats,
    trialPeriodEnd,
    daysTillEnd,
    showEdit,
    newTrialPeriodEnd,
    trialDuration,
    auditLevel,
    showEditAudit,
    isMobileUser,
    setViewingPartnerCode,
  ]);

  const renderFacebookDeletionCard = () => {
    if (!user) {
      return null;
    }

    if (facebookDeletionLoading) {
      return <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 text-sm text-purple-800">Loading Facebook deletion status…</div>;
    }

    if (facebookDeletionError) {
      return <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700">{facebookDeletionError}</div>;
    }

    if (!facebookDeletionStatus || !facebookDeletionStatus.status || facebookDeletionStatus.status === "not_scheduled") {
      return null;
    }

    if (facebookDeletionStatus.status === "scheduled") {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="text-yellow-900 font-semibold text-lg mb-2">Facebook deletion scheduled</h3>
          <p className="text-sm text-yellow-800 mb-1">Scheduled for {formatDateTime(facebookDeletionStatus.scheduledAt)}.</p>
          {facebookDeletionStatus.requestedAt && <p className="text-sm text-yellow-800 mb-3">Requested on {formatDateTime(facebookDeletionStatus.requestedAt)}.</p>}
          <div className="flex items-center gap-3">
            <SudoButton
              onClick={handleCancelFacebookDeletion}
              disabled={facebookDeletionActionLoading}
              variant="secondary"
              size="small"
            >
              {facebookDeletionActionLoading ? "Cancelling…" : "Cancel scheduled deletion"}
            </SudoButton>
            {facebookDeletionStatus.facebookUserId && <span className="text-xs text-yellow-900">Facebook ID: {facebookDeletionStatus.facebookUserId}</span>}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <h3 className="text-gray-900 font-semibold text-lg mb-2">Facebook deletion status</h3>
        <p className="text-sm text-gray-800">Status: {facebookDeletionStatus.status}</p>
        {facebookDeletionStatus.completedAt && <p className="text-sm text-gray-800">Completed on {formatDateTime(facebookDeletionStatus.completedAt)}</p>}
        {facebookDeletionStatus.canceledAt && <p className="text-sm text-gray-800">Canceled on {formatDateTime(facebookDeletionStatus.canceledAt)}</p>}
        {facebookDeletionStatus.statusReason && <p className="text-sm text-gray-700 mt-1">Reason: {facebookDeletionStatus.statusReason}</p>}
      </div>
    );
  };

  if (!user && !notFound) {
    return (
      <div className="flex items-center justify-center p-16">
        <h1 className="text-4xl text-gray-600">Loading...</h1>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex items-center justify-center p-16">
        <h1 className="text-3xl text-red-600">User not found: {emailOrId}</h1>
      </div>
    );
  }

  return (
    <div>
      {user ? (
        <div className="user p-2 max-w-7xl mx-auto">
          <div className="bg-white flex flex-col lg:flex-row gap-4 rounded-lg border border-gray-200 mb-6">
            <div className="flex flex-col md:flex-row gap-4 w-full md:w-1/2">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={user?.photoURL || user?.providerData?.[0]?.photoURL}
                    className="w-12 h-12 rounded-full"
                    alt="User avatar"
                  />
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-1">{user.displayName || user.email}</h1>
                    <div className="flex gap-2">
                      <SudoButton
                        onClick={(e) => {
                          e.preventDefault();
                          setShowEditDisplayName(!showEditDisplayName);
                          setDisplayNameMessage("");
                          setNewDisplayName(user.displayName || "");
                        }}
                        variant="secondary"
                        size="xsmall"
                      >
                        Edit Name
                      </SudoButton>
                      <CopyToClipboard
                        text={window.location.href}
                        onCopy={() => setToast(`User page link copied to clipboard`)}
                      >
                        <SudoButton
                          variant="tertiary"
                          size="xsmall"
                          leftIcon="/assets/copy.svg"
                          iconSize={16}
                          aria-label="Copy user page link"
                        >
                          Copy Link
                        </SudoButton>
                      </CopyToClipboard>
                    </div>
                  </div>
                </div>

                {subscription?.usesLedgers && ledgerBalanceForSync !== null && normalizedTotalQuota !== null && !ledgerInSync ? (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <p className="font-semibold mb-1">Ledger Balance Mismatch</p>
                        <p className="mb-0">
                          Displayed quota ({Utils.numberWithCommas(normalizedTotalQuota)}) differs from the current ledger balance ({Utils.numberWithCommas(ledgerBalanceForSync)}). Sync them to avoid
                          confusing UI totals.
                        </p>
                      </div>
                      <SudoButton
                        disabled={ledgerSyncButtonDisabled}
                        onClick={(e) => {
                          e.preventDefault();
                          setToolsEnabled(false);
                          Auth.fetchWithIdToken(`/api/admin/user/${subscription.id}/sync-ledger`, { method: "POST" })
                            .then(() => {
                              setToolsEnabled(true);
                              window.location.reload();
                            })
                            .catch((err) => {
                              setToolsEnabled(true);
                              alert(err.message || "Failed to sync ledger");
                            });
                        }}
                        variant="secondary"
                        size="small"
                        state={!toolsEnabled || ledgerBalanceLoading ? "loading" : "default"}
                      >
                        Sync Ledger Balance
                      </SudoButton>
                    </div>
                  </div>
                ) : null}

                <UserFieldGrid
                  fields={userFields}
                  categoryOrder={["Account Info", "Subscription Info", "Quota & Usage", "Settings & Flags", "Stats & Metrics", "Trial & Audit", "Partner Program"]}
                />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm  mb-6 w-full md:w-1/2">
              <div className="p-6">
                <div className="flex flex-col gap-6">
                  <div className="flex-1">
                    {adminTools && <UserInsights user={user} />}
                  </div>
                  <div className="flex-1">
                    <UserNotes
                      user={user}
                      userCache={props.userCache}
                    />
                  </div>
                  <div className="flex-1">
                    {subscriptionEvents && (
                      <div className="subscription-events rounded-xl flex flex-col gap-4">
                        <div className="bg-gray-50 p-4 border border-gray-200 rounded-lg ">
                          <h3 className="text-lg font-medium mb-4">Subscription Events</h3>
                          <div
                            className="rounded-lg"
                            style={{ maxHeight: "500px", overflowY: "auto", overflowX: "hidden" }}
                          >
                            <ul className="space-y-3">
                              {subscriptionEvents.map((event, i) => {
                                return (
                                  <li
                                    key={event.id}
                                    className="text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm cursor-pointer transition hover:shadow-md active:scale-[0.99] break-words"
                                    onClick={() => setSelectedSubscriptionEvent(event)}
                                  >
                                    <span className="font-mono text-xs text-gray-500 block mb-1">{formatDateTimeWithTimezone(event.get("createdAt").toDate())}</span>
                                    <span className="text-gray-900">{event.get("note") || event.get("eventType") || event.get("source") || "Subscription updated"}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                        {subscription.usesLedgers && (
                          <div className="mt-4 bg-gray-50 p-4 border border-gray-200 rounded-lg ">
                            <h3 className="text-lg font-medium mb-4">Ledger</h3>
                            <Ledgers subscription={subscription} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {renderFacebookDeletionCard()}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="p-6">
              <h3 className="text-lg font-medium mb-6">Tools</h3>

              {/* Active Checkout Sessions Section */}
              <div className="mb-8">
                <h4 className="text-base font-medium mb-4 text-gray-900 font-bold text-lg">Active Checkout Sessions</h4>
                <div className="flex gap-6 mb-4">
                  <SudoButton
                    onClick={fetchActiveCheckoutSessions}
                    disabled={loadingSessions}
                    variant="primary"
                    size="default"
                    state={loadingSessions ? "loading" : "default"}
                  >
                    {loadingSessions ? "Loading..." : "Refresh Sessions"}
                  </SudoButton>
                  <SudoButton
                    onClick={async () => {
                      await confirmAction({
                        title: "Expire all checkout sessions",
                        message: `Are you sure you want to expire all checkout sessions for ${user.email}?`,
                        actionLabel: "Expire all sessions",
                        destructive: true
                      }, async () => {
                        await expireAllCheckoutSessions();
                      });
                    }}
                    disabled={!toolsEnabled}
                    variant="danger"
                    size="default"
                  >
                    Expire All Sessions
                  </SudoButton>
                </div>

                {activeCheckoutSessions?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse bg-white border border-gray-200 rounded-lg">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Created</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Expires</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Products</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Amount</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Status</th>
                          <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeCheckoutSessions.map((session) => (
                          <tr
                            key={session.id}
                            className="hover:bg-gray-50"
                          >
                            <td className="p-3 text-sm text-gray-900 border-b border-gray-100">{dateFormat(new Date(session.created), "mm/dd/yy HH:MM")}</td>
                            <td className="p-3 text-sm text-gray-900 border-b border-gray-100">{session.expires_at ? dateFormat(new Date(session.expires_at), "mm/dd/yy HH:MM") : "N/A"}</td>
                            <td className="p-3 text-sm text-gray-900 border-b border-gray-100">{session.products || "Unknown product"}</td>
                            <td className="p-3 text-sm text-gray-900 border-b border-gray-100">
                              {session.amount_total ? `${(session.amount_total / 100).toFixed(2)} ${session.currency.toUpperCase()}` : "N/A"}
                            </td>
                            <td className="p-3 text-sm text-gray-900 border-b border-gray-100">{session.payment_status}</td>
                            <td className="p-3 text-sm text-gray-900 border-b border-gray-100">
                              <div className="flex items-center gap-4">
                                <a
                                  href={session.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-app-primary-cta hover:text-app-primary-cta-dark hover:underline text-sm"
                                >
                                  View
                                </a>
                                <SudoButton
                                  onClick={async () => {
                                    await confirmAction({
                                      title: "Expire checkout session",
                                      message: `Are you sure you want to expire checkout session ${session.id}?`,
                                      actionLabel: "Expire session",
                                      destructive: true
                                    }, async () => {
                                      await expireSpecificSession(session.id);
                                    });
                                  }}
                                  disabled={!toolsEnabled}
                                  variant="danger"
                                  size="small"
                                >
                                  Expire
                                </SudoButton>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm">{activeCheckoutSessions === null ? "Loading sessions..." : "No active checkout sessions found."}</p>
                )}
              </div>

              {user && (
                <div className="mb-8">
                  <StripeLinkedAccounts
                    user={user}
                    refreshUser={getUser}
                  />
                </div>
              )}

              {user && (
                <div className="mb-8">
                  <StripeEmailCollisionWarning user={user} />
                </div>
              )}

              {/* Admin Actions */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Admin Actions</h3>
                <a className="block mb-4 underline" href={`/admin/logs?view=actions&targetId=${encodeURIComponent(user.uid)}`}>View activity for this user</a>

                <div className="space-y-6">
                  {/* Subscription Management */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="text-base font-semibold text-purple-900 mb-2">Subscription Management</h4>
                    <p className="text-sm text-purple-700 mb-4">Actions that sync and manage user's subscription and billing integrations.</p>
                    <div className="flex flex-wrap gap-6">
                      <SudoTooltip text="Pull the latest plan + billing state from both Stripe and RevenueCat to resolve subscription mismatches.">
                        <SudoButton
                          disabled={!toolsEnabled}
                          onClick={(e) => {
                            e.preventDefault();
                            setToolsEnabled(false);
                            Auth.fetchWithIdToken(`/api/subscriptions/${subscription.id}/syncAll`, {})
                              .then(() => {
                                setToolsEnabled(true);
                                window.location.reload();
                              })
                              .catch(async (e) => {
                                setToolsEnabled(true);
                                let msg = e.message;
                                let code;
                                try {
                                  const body = await e.response?.clone().json();
                                  if (body?.message) msg = body.message;
                                  code = body?.code;
                                } catch (_) {}

                                if (code === "DUAL_SUBSCRIPTION_CONFLICT") {
                                  alert("User has active subscriptions on both Stripe and RevenueCat. Cancel one before syncing.");
                                } else if (msg?.includes("Updating to invalid plan:")) {
                                  alert("User plan does not exist or has been archived. Please migrate user to a valid plan and try again.");
                                } else {
                                  alert(msg);
                                }
                              });
                          }}
                          variant="secondary"
                          size="default"
                        >
                          Sync Stripe/Revcat
                        </SudoButton>
                      </SudoTooltip>
                      <SudoTooltip text="Re-run provisioning to recreate missing subscription records, entitlements, or quotas for this user.">
                        <SudoButton
                          disabled={!toolsEnabled}
                          onClick={(e) => {
                            e.preventDefault();
                            setToolsEnabled(false);
                            Auth.fetchWithIdToken(`/api/subscriptions/${subscription.id}/provision`, {})
                              .then((res) => {
                                setToolsEnabled(true);
                                window.location.reload();
                              })
                              .catch((e) => {
                                setToolsEnabled(true);
                                alert(e.message);
                              });
                          }}
                          variant="secondary"
                          size="default"
                        >
                          Provision Subscription
                        </SudoButton>
                      </SudoTooltip>
                      {subscription && (
                        <SudoTooltip text="Checks Stripe and clears stale tax-location issue fields if billing tax is resolved.">
                          <SudoButton
                            disabled={!toolsEnabled || syncingTaxInformation}
                            onClick={(e) => {
                              e.preventDefault();
                              syncTaxInformation();
                            }}
                            variant="secondary"
                            size="default"
                            state={syncingTaxInformation ? "loading" : "default"}
                          >
                            Sync Tax Information
                          </SudoButton>
                        </SudoTooltip>
                      )}
                      {subscription.data.cancelAtPeriodEnd && (
                        <SudoTooltip text="Remove the scheduled cancellation and keep the subscription active. Use this instead of the Stripe dashboard.">
                          <SudoButton
                            disabled={!toolsEnabled}
                            onClick={async e => {
                              e.preventDefault();
                              await confirmAction({
                                title: "Un-cancel subscription",
                                message: "This will remove the scheduled cancellation and keep the subscription active. Are you sure?",
                                actionLabel: "Keep subscription active",
                                destructive: false
                              }, async () => {
                                setToolsEnabled(false);
                                await Auth.fetchWithIdToken(`/api/subscriptions/${subscription.id}/uncancel`, {}).then(res => {
                                  setToolsEnabled(true);
                                  window.location.reload();
                                }).catch(e => {
                                  setToolsEnabled(true);
                                  alert(e.message);
                                });
                              });
                            }}
                            variant="secondary"
                            size="default"
                          >
                            Un-cancel Subscription
                          </SudoButton>
                        </SudoTooltip>
                      )}
                      {subscription && (
                        <AnnualCreditsRepair
                          key={subscription.id}
                          userId={subscription.id}
                          toolsEnabled={toolsEnabled}
                          setToolsEnabled={setToolsEnabled}
                          onRepaired={async () => {
                            delete props.userCache.users[subscription.id];
                            await getUser();
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <CustomerIoActions
                    userId={user?.uid}
                    setToast={setToast}
                  />
                  {can("users.updateBilling") && <TrialFreeChatTesting
                    userId={user?.uid}
                    subscription={subscription}
                    toolsEnabled={toolsEnabled}
                    setToast={setToast}
                    onUpdated={getUser}
                  />}
                  {can("users.updateBilling") && <TrialStatusToggle
                    userId={user?.uid}
                    subscription={subscription}
                    toolsEnabled={toolsEnabled}
                    setToast={setToast}
                    onUpdated={getUser}
                  />}
                  {adminTools && user?.uid && <TrialAIReview
                    key={user.uid}
                    userId={user.uid}
                    subscription={subscription}
                    toolsEnabled={toolsEnabled}
                    onUpdated={async () => {
                      delete props.userCache.users[user.uid];
                      await getUser();
                    }}
                  />}

                  {/* User Account Actions */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="text-base font-semibold text-purple-900 mb-2">User Account Actions</h4>
                    <p className="text-sm text-purple-700 mb-4">Actions that directly interact with or impersonate the user account.</p>
                    <div className="flex flex-wrap gap-6">
                      {can("users.impersonate") && (role === "admin" || !user.adminRole) && <SudoTooltip text="Logs you into the product as this user for debugging; avoid edits and log out quickly.">
                        <SudoButton
                          onClick={async e => {
                            e.preventDefault();
                            await confirmAction({
                              title: "Become user",
                              message: "Becoming a user can be very dangerous. Avoid changing editing docs or changing data while logged in as the user. And remember to log out as soon as you can. Are you ready?",
                              actionLabel: "Become user",
                              destructive: false
                            }, async () => {
                              const result = await Auth.fetchWithIdToken("/api/admin/become", {
                                uid: user.uid
                              });
                              await firebase.auth().signInWithCustomToken(result.token);
                              // Drop the admin page and its in-memory customer data.
                              // Sign-in failures propagate to the confirmation dialog.
                              window.location.replace("/app");
                            });
                          }}
                          variant="warning"
                          size="default"
                        >
                          Become User
                        </SudoButton>
                      </SudoTooltip>}

                      <SudoTooltip text="Requires this user to sign in again on all devices when their current session token expires.">
                        <SudoButton
                          variant="warning"
                          size="default"
                          state={loggingOutSessions ? "loading" : "default"}
                          onClick={async () => {
                            await confirmAction({
                              title: "Log Out User Sessions",
                              message: `Log out existing sessions for ${user.email || user.uid} on all devices? Current access may continue for up to one hour, and open editor connections may remain connected. This does not disable the account or prevent a new sign-in.`,
                              actionLabel: "Log Out User Sessions",
                              destructive: true,
                            }, async () => {
                              setLoggingOutSessions(true);
                              try {
                                const result = await Auth.fetchWithIdToken("/api/admin/logout-user-sessions", { uid: user.uid });
                                if (!result.success) {
                                  throw new Error(result.message || "Unable to log out user sessions. Please try again.");
                                }
                                setToast({ type: "success", message: "Session renewal revoked. Existing access may continue for up to one hour; open editor connections may remain connected." });
                              } finally {
                                setLoggingOutSessions(false);
                              }
                            });
                          }}
                        >
                          Log Out User Sessions
                        </SudoButton>
                      </SudoTooltip>

                      {can("users.impersonate") && (role === "admin" || !user.adminRole) && <SudoTooltip text="Creates a one-time login link to share with the user after verifying ownership.">
                        <SudoButton
                          onClick={(e) => {
                            setGeneratingLink(true);
                            e.preventDefault();

                            Auth.fetchWithIdToken("/api/admin/signinLink", {
                              email: user.email,
                            })
                              .then((result) => {
                                setLink(result.link);
                              })
                              .finally(() => {
                                setGeneratingLink(false);
                              });
                          }}
                          variant="secondary"
                          size="default"
                          state={generatingLink ? "loading" : "default"}
                        >
                          Generate Sign-in Link
                        </SudoButton>
                      </SudoTooltip>}
                    </div>
                  </div>
                  {link ? (
                    <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex items-center gap-4 mb-2">
                        <p className="text-sm font-mono break-all">{link}</p>
                        <CopyToClipboard
                          text={link}
                          onCopy={() => setToast(`Link copied to clipboard`)}
                        >
                          <SudoButton
                            variant="tertiaryGrey"
                            size="small"
                            leftIcon="/assets/copy.svg"
                            iconSize={16}
                            aria-label="Copy link"
                          >
                            Copy
                          </SudoButton>
                        </CopyToClipboard>
                      </div>
                      <p className="text-xs text-red-800 font-medium">WARNING: treat this link like a password! If giving to a user, verify that the user actually owns the account in question</p>
                    </div>
                  ) : null}
                  {/* Content & Permissions */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="text-base font-semibold text-purple-900 mb-2">Content & Permissions</h4>
                    <p className="text-sm text-purple-700 mb-4">Actions that affect user's content creation and sharing permissions.</p>
                    <div className="flex flex-wrap gap-6">
                      <SudoTooltip text="Enable or block sharing links for this user when abuse or spam is suspected.">
                        <SudoButton
                          disabled={!toolsEnabled}
                          onClick={async e => {
                            e.preventDefault();
                            const isBanned = subscription && subscription.isBannedFromSharingDocuments;
                            const action = isBanned ? "unban" : "ban";
                            await confirmAction({
                              title: "Change document sharing access",
                              message: `This will ${action} the user from sharing documents. Are you sure?`,
                              actionLabel: "Confirm",
                              destructive: true
                            }, async () => {
                              setToolsEnabled(false);
                              await Auth.fetchWithIdToken("/api/admin/banUserFromSharingDocuments", {
                                uid: user.uid,
                                ban: !isBanned
                              }).then(result => {
                                alert(`Success! User has been ${action}ned from sharing documents.`);
                                window.location.reload();
                              }).catch(async ({
                                response
                              }) => {
                                const json = await response.json();
                                alert(json.message);
                                window.location.reload();
                              });
                            });
                          }}
                          variant="warning"
                          size="default"
                        >
                          {subscription && subscription.isBannedFromSharingDocuments ? "Unban From Sharing Documents" : "Ban From Sharing Documents"}
                        </SudoButton>
                      </SudoTooltip>

                      {adminTools && <SudoTooltip text="Regenerate the default onboarding demo workspace for this account.">
                        <SudoButton
                          onClick={(e) => {
                            e.preventDefault();
                            handleCreateDemoProject();
                          }}
                          disabled={creatingDemoProject}
                          variant="secondary"
                          size="default"
                          state={creatingDemoProject ? "loading" : "default"}
                        >
                          Create Demo Project
                        </SudoButton>
                      </SudoTooltip>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Danger Section - Collapsible */}
              {can("users.updateBilling") && <div className="mt-8 p-4 bg-red-100 rounded-lg border border-red-200">
                <CollapsableContainer
                  title="⚠️ Danger Zone"
                  defaultCollapse={true}
                >
                  <div className="p-4 bg-red-100 rounded-lg border border-red-300">
                    <div className="bg-red-100 p-4 rounded-md border border-red-300 mb-6">
                      <p className="text-sm font-medium text-red-800">⚠️ WARNING: These actions are irreversible and potentially destructive. Expand this section only when necessary.</p>
                    </div>

                    {/* Subscription Reset Section */}
                    {subscription && !subscription.activeSubscription() && (
                      <div className="mb-8 p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <h4 className="text-base font-semibold text-orange-900 mb-2">Subscription Reset</h4>
                        <p className="text-sm text-orange-700 mb-4">Resets user's subscription state and grants fresh trial quota. User will lose any existing quota/credits.</p>
                        <SudoButton
                          disabled={!toolsEnabled}
                          onClick={async e => {
                            e.preventDefault();
                            const confirmMessage = `This will restore ${user.email || user.uid} to trial status with fresh trial quota. This will reset their subscription state. Are you sure?`;
                            await confirmAction({
                              title: "Restore user to trial",
                              message: confirmMessage,
                              actionLabel: "Restore to trial",
                              destructive: true
                            }, async () => {
                              setToolsEnabled(false);
                              await Auth.fetchWithIdToken(`/api/admin/user/${user.uid}/restoreToTrial`, {
                                version: DEFAULT_PRICING_VERSION // Default to version 4.2 which has reasonable trial quota
                              }).then(result => {
                                alert(`Success! User restored to trial version ${result.version}.`);
                                window.location.reload();
                              }).catch(async ({
                                response
                              }) => {
                                const json = await response.json();
                                alert(`Error: ${json.message || "Failed to restore user to trial"}`);
                                setToolsEnabled(true);
                              });
                            });
                          }}
                          variant="danger"
                          size="default"
                        >
                          Restore to Trial
                        </SudoButton>
                      </div>
                    )}

                    {/* Account Deletion Section */}
                    {adminTools && <div className="p-4 bg-red-50 rounded-lg border-2 border-red-300">
                      <h4 className="text-base font-semibold text-red-900 mb-2">Permanent Account Deletion</h4>
                      <p className="text-sm text-red-700 mb-4">⚠️ IRREVERSIBLE: This will permanently delete the user account and archive all their content. This action cannot be undone.</p>
                      <SudoButton
                        disabled={!toolsEnabled}
                        onClick={async e => {
                          e.preventDefault();
                          await confirmAction({
                            title: "Permanently delete user",
                            message: "This will permanently delete the user and archive their content. This action is IRREVERSIBLE. Are you absolutely sure?",
                            actionLabel: "Delete user permanently",
                            destructive: true
                          }, async () => {
                            setToolsEnabled(false);
                            await Auth.fetchWithIdToken("/api/admin/deleteUser", {
                              uid: user.uid
                            }).then(result => {
                              alert("Success! User has been deleted.");
                              window.location.reload();
                            }).catch(async ({
                              response
                            }) => {
                              const json = await response.json();
                              alert(json.message);
                              window.location.reload();
                            });
                          });
                        }}
                        variant="danger"
                        size="default"
                      >
                        Delete User Permanently
                      </SudoButton>
                    </div>}
                  </div>
                </CollapsableContainer>
              </div>}

              {demoProject && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800 mb-2">Demo project created successfully!</p>
                  <p>
                    <a
                      href={demoProject.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-app-primary-cta hover:text-app-primary-cta-dark hover:underline text-sm"
                    >
                      Open project in new tab ↗
                    </a>
                  </p>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-gray-200">
                <ManagePlans
                  onToolButtonClick={onToolButtonClick}
                  toolsEnabled={toolsEnabled}
                  subscription={subscription}
                />
              </div>
            </div>
          </div>

          {adminTools ? <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="p-6">
              <nav className="flex flex-wrap gap-2">
                <NavLink
                  exact
                  to={`/admin/user/${emailOrId}`}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  activeClassName="bg-app-primary-cta-transparent-15 border-app-primary text-app-primary"
                >
                  Cardsets
                </NavLink>
                <NavLink
                  to={`/admin/user/${emailOrId}/invocations`}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  activeClassName="bg-app-primary-cta-transparent-15 border-app-primary text-app-primary"
                >
                  Invocations
                </NavLink>
                <NavLink
                  to={`/admin/user/${emailOrId}/completions`}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  activeClassName="bg-app-primary-cta-transparent-15 border-app-primary text-app-primary"
                >
                  Completions
                </NavLink>
                <NavLink
                  exact
                  to={`/admin/user/${emailOrId}/series`}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  activeClassName="bg-app-primary-cta-transparent-15 border-app-primary text-app-primary"
                >
                  Series
                </NavLink>
                <NavLink
                  exact
                  to={`/admin/user/${emailOrId}/projects`}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  activeClassName="bg-app-primary-cta-transparent-15 border-app-primary text-app-primary"
                >
                  Projects
                </NavLink>
                <NavLink
                  exact
                  to={`/admin/user/${emailOrId}/documents`}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  activeClassName="bg-app-primary-cta-transparent-15 border-app-primary text-app-primary"
                >
                  Documents
                </NavLink>
                <NavLink
                  exact
                  to={`/admin/user/${emailOrId}/shared-documents`}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  activeClassName="bg-app-primary-cta-transparent-15 border-app-primary text-app-primary"
                >
                  Shared Documents
                </NavLink>
                <NavLink
                  exact
                  to={`/admin/user/${emailOrId}/chats`}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  activeClassName="bg-app-primary-cta-transparent-15 border-app-primary text-app-primary"
                >
                  Chats
                </NavLink>
              </nav>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <Switch>
              <Route
                exact
                path={`/admin/user/${emailOrId}`}
              >
                <CardSets
                  user={user}
                  userCache={props.userCache}
                  showUserColumn={false}
                />
              </Route>

              <Route path={`/admin/user/${emailOrId}/completions`}>
                <div className="mb-6">
                  <CompletionsSearch user={user} />
                </div>
                <Completions
                  userId={user.uid}
                  userCache={props.userCache}
                  showUserColumn={false}
                />
              </Route>

              <Route path={`/admin/user/${emailOrId}/invocations`}>
                <Invocations
                  userId={user.uid}
                  userCache={props.userCache}
                  showUserColumn={false}
                />
              </Route>

              <Route path={`/admin/user/${emailOrId}/documents`}>
                <Documents
                  userCache={props.userCache}
                  user={user}
                  showUserColumn={false}
                />
              </Route>

              <Route path={`/admin/user/${emailOrId}/shared-documents`}>
                <SharedDocuments
                  userCache={props.userCache}
                  user={user}
                />
              </Route>

              <Route path={`/admin/user/${emailOrId}/chats`}>
                <AgenticChatList
                  userId={user.uid}
                  onSelectChat={(chatId) => setViewingChatId(chatId)}
                />
              </Route>

              <Route path={`/admin/user/${emailOrId}/projects`}>
                <Projects
                  userCache={props.userCache}
                  user={user}
                  showUserColumn={false}
                />
              </Route>

              <Route path={`/admin/user/${emailOrId}/series`}>
                <Series user={user} />
              </Route>
            </Switch>
          </div>
          </> : <>
            <UserDiagnostics userId={user.uid} />
            {can("users.moderateSharing") && <section className="bg-white border rounded-lg p-5 mt-6">
              <h2 className="text-xl font-semibold">Shared document moderation</h2>
              <p className="text-sm text-gray-600">Manage published links using their IDs and status. Document content is excluded.</p>
              <SharedDocuments user={user} />
            </section>}
          </>}
        </div>
      ) : null}

      <PartnerCodeModal
        partnerCode={viewingPartnerCode}
        onClose={() => setViewingPartnerCode(null)}
      />

      <SubscriptionEventModal
        event={selectedSubscriptionEvent}
        onClose={() => setSelectedSubscriptionEvent(null)}
      />

      {canReadChats && user && (
        <AgenticChatDetailModal
          userId={user.uid}
          chatId={viewingChatId}
          onClose={() => setViewingChatId(null)}
        />
      )}
    </div>
  );
}

function PartnerCodeModal({ partnerCode, onClose }) {
  const { data: partnerCodeData, isLoading } = usePartnerCodeByCode(partnerCode || "");

  return (
    <Dialog
      open={!!partnerCode}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="sm:max-w-xl">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-gray-600">Loading partner code details...</p>
          </div>
        ) : partnerCodeData ? (
          <PartnerCodeDetails
            code={partnerCodeData}
            onClose={onClose}
            onEdit={() => {}}
          />
        ) : (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-gray-600">Partner code not found</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionEventModal({ event, onClose }) {
  if (!event) return null;

  const eventData = event.data();
  const createdAt = eventData.createdAt?.toDate ? eventData.createdAt.toDate() : eventData.createdAt;

  return (
    <Dialog
      open={!!event}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="sm:max-w-4xl h-[80vh] overflow-hidden flex flex-col">
        <div className="flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Subscription Event Details</h2>
          <div className="mb-4 space-y-1">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Date:</span> {formatDateTimeWithTimezone(createdAt)}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Note:</span> {eventData.note}
            </p>
            {eventData.eventType && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">Event Type:</span> {eventData.eventType}
              </p>
            )}
            {eventData.caller && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">Caller:</span> {eventData.caller}
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <h3 className="text-base font-medium text-gray-900 mb-2">Subscription State at Time of Event</h3>
          <div className="rounded-lg border border-gray-200 overflow-auto max-h-[calc(80vh-250px)]">
            <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-words text-left">
              <JSONTree
                data={eventData.subscription}
                invertTheme
                theme={{
                  base00: "#00000000",
                  base01: "#223b54",
                  base02: "#405c79",
                  base03: "#627e99",
                  base04: "#aabcce",
                  base05: "#cbd6e2",
                  base06: "#e5ebf1",
                  base07: "#f7f9fb",
                  base08: "#bf8b56",
                  base09: "#bfbf56",
                  base0A: "#8bbf56",
                  base0B: "#56bf8b",
                  base0C: "#568bbf",
                  base0D: "#8b56bf",
                  base0E: "#bf568b",
                  base0F: "#bf5656",
                }}
                postprocessValue={(value) => {
                  if (value && typeof value === "object" && Object.keys(value).length === 2 && Object.keys(value).includes("nanoseconds") && Object.keys(value).includes("seconds")) {
                    return formatDateTimeWithTimezone(new Date(value.seconds * 1000));
                  }

                  return value;
                }}
              />
            </pre>
          </div>
        </div>

        <div className="flex-shrink-0 mt-4 flex justify-end gap-2">
          <CopyToClipboard
            text={JSON.stringify(eventData.subscription, null, 2)}
            onCopy={() => alert("Subscription JSON copied to clipboard")}
          >
            <SudoButton
              variant="secondary"
              size="small"
              leftIcon="/assets/copy.svg"
              iconSize={16}
            >
              Copy JSON
            </SudoButton>
          </CopyToClipboard>
          <SudoButton
            onClick={onClose}
            variant="primary"
            size="small"
          >
            Close
          </SudoButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
